"use client";

import { useEffect } from "react";
import { hasAnalyticsConsent } from "@/lib/gtag";

/**
 * Wstrzykuje piksele marketingowe (Meta/Facebook, Microsoft Clarity, TikTok,
 * LinkedIn) imperatywnie w useEffect — komponent ZAWSZE zwraca null i nie
 * zmienia kształtu drzewa po hydratacji (analogicznie do GoogleAnalytics,
 * co chroni reconciler React 19 przed „removeChild … parentNode is null”).
 *
 * Wszystkie piksele ładują się dopiero po zgodzie na cookies i są pomijane
 * w środowisku deweloperskim. Brak lokalnego stanu — unika ostrzeżeń
 * set-state-in-effect i podwójnych renderów.
 */
export default function MarketingPixels({
  facebookPixelId,
  clarityProjectId,
  tiktokPixelId,
  linkedInPartnerId,
}: {
  facebookPixelId?: string;
  clarityProjectId?: string;
  tiktokPixelId?: string;
  linkedInPartnerId?: string;
}) {
  useEffect(() => {
    if (process.env.NODE_ENV === "development") return;

    const inject = () => {
      if (!hasAnalyticsConsent()) return;

      // ── Meta / Facebook Pixel ──
      if (facebookPixelId && !document.getElementById("fb-pixel")) {
        const s = document.createElement("script");
        s.id = "fb-pixel";
        s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${facebookPixelId}');fbq('track','PageView');`;
        document.head.appendChild(s);
      }

      // ── Microsoft Clarity ──
      if (clarityProjectId && !document.getElementById("ms-clarity")) {
        const s = document.createElement("script");
        s.id = "ms-clarity";
        s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${clarityProjectId}");`;
        document.head.appendChild(s);
      }

      // ── TikTok Pixel ──
      if (tiktokPixelId && !document.getElementById("tiktok-pixel")) {
        const s = document.createElement("script");
        s.id = "tiktok-pixel";
        s.text = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=d.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${tiktokPixelId}');ttq.page();}(window,document,'ttq');`;
        document.head.appendChild(s);
      }

      // ── LinkedIn Insight Tag ──
      if (linkedInPartnerId && !document.getElementById("linkedin-insight")) {
        const cfg = document.createElement("script");
        cfg.id = "linkedin-insight";
        cfg.text = `_linkedin_partner_id="${linkedInPartnerId}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];var b=document.createElement("script");b.type="text/javascript";b.async=true;b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";s.parentNode.insertBefore(b,s)})(window.lintrk);`;
        document.head.appendChild(cfg);
      }
    };

    inject();
    const onConsent = () => inject();
    window.addEventListener("cookie-consent", onConsent);
    return () => window.removeEventListener("cookie-consent", onConsent);
  }, [facebookPixelId, clarityProjectId, tiktokPixelId, linkedInPartnerId]);

  return null;
}
